import { useCallback, useEffect, useRef, useState } from "react";
import { Entities } from "../api/Entities";
import { Effort, EffortType, Mode, Rec, RepScheme, RepSchemeType, Tecnica, TimeUnderTension } from "../types/Entities";

type SelectOption = { label: string; value: string | number };
type StringOption = { label: string; value: string };

export type EntityOptionsState = {
  repSchemeTypes: SelectOption[];
  effortTypes: SelectOption[];
  tuts: SelectOption[];
  recs: SelectOption[];
  modes: SelectOption[];
  tecniche: SelectOption[];
  tecnicheRaw: Tecnica[];
};

const EMPTY: EntityOptionsState = {
  repSchemeTypes: [],
  effortTypes: [],
  tuts: [],
  recs: [],
  modes: [],
  tecniche: [],
  tecnicheRaw: [],
};

// `enabled` gates the admin-only catalog fetches. The entity catalog endpoints
// (rep-scheme types, efforts, TUT, tecniche…) are isAdmin-gated on the BE, so
// calling them as an athlete returns 401 and bounces to login. Read-only
// viewers (athletes) pass enabled=false to skip every request.
export function useEntityOptions(enabled: boolean = true) {
  const [options, setOptions] = useState<EntityOptionsState>(EMPTY);
  const [loading, setLoading] = useState(enabled);

  // PROG-WEEK-PARAMS/FE-1 — per-typeId caches for the rep-scheme / effort
  // catalogs. Memoize the in-flight promise per typeId so concurrent callers
  // (e.g. N exercise editors hydrating in the same tick) share one round-trip.
  const repSchemeCacheRef = useRef<Map<number, Promise<StringOption[]>>>(new Map());
  const effortCacheRef = useRef<Map<number, Promise<StringOption[]>>>(new Map());

  const loadRepSchemesByType = useCallback((typeId: number): Promise<StringOption[]> => {
    if (!typeId) return Promise.resolve([]);
    const cached = repSchemeCacheRef.current.get(typeId);
    if (cached) return cached;
    const p = Entities.getRepSchemesByType(typeId).then(({ data }) => {
      const items = (data as any).items ?? (data as any).list ?? [];
      return items.map((r: RepScheme) => ({ label: r.repScheme, value: r.repScheme }));
    });
    repSchemeCacheRef.current.set(typeId, p);
    return p;
  }, []);

  const loadEffortsByType = useCallback((typeId: number): Promise<StringOption[]> => {
    if (!typeId) return Promise.resolve([]);
    const cached = effortCacheRef.current.get(typeId);
    if (cached) return cached;
    const p = Entities.getEffortsByType(typeId).then(({ data }) => {
      const items = (data as any).items ?? (data as any).list ?? [];
      return items.map((e: Effort) => ({ label: e.effort, value: e.effort }));
    });
    effortCacheRef.current.set(typeId, p);
    return p;
  }, []);

  useEffect(() => {
    // Read-only viewers (athletes) must not touch the admin-only catalog
    // endpoints — they 401 and bounce to login.
    if (!enabled) {
      setOptions(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.allSettled([
      Entities.getRepSchemeTypes(),
      Entities.getEffortTypes(),
      Entities.getTUTs(),
      Entities.getRecs(),
      Entities.getModes(),
      // Load the full tecnica catalog (not just the default first page of 10) —
      // otherwise a tecnica beyond page 1 can't be resolved by lookupTecnica,
      // so its xReps / mini-set fields never surface on a set.
      Entities.getPaginatedTecniche(new URLSearchParams({ size: "1000" })),
    ]).then(([rstRes, effRes, tutRes, recRes, modeRes, tecRes]) => {
      const rawTecniche: Tecnica[] =
        tecRes.status === "fulfilled"
          ? ((tecRes.value.data as any).items ?? (tecRes.value.data as any).list ?? [])
          : [];

      setOptions({
        repSchemeTypes:
          rstRes.status === "fulfilled"
            ? rstRes.value.data.list.map((r: RepSchemeType) => ({
                label: r.repSchemeType,
                value: r.id,
              }))
            : [],
        effortTypes:
          effRes.status === "fulfilled"
            ? effRes.value.data.list.map((e: EffortType) => ({
                label: e.effortType,
                value: e.id,
              }))
            : [],
        tuts:
          tutRes.status === "fulfilled"
            ? tutRes.value.data.list.map((t: TimeUnderTension) => ({
                label: `${t.ecc}-${t.stopEcc}-${t.conc}-${t.stopConc}`,
                value: t.id,
              }))
            : [],
        recs:
          recRes.status === "fulfilled"
            ? ((recRes.value.data as any).list ?? []).map((r: Rec) => ({
                label: `${r.rec}s`,
                value: r.id,
              }))
            : [],
        modes:
          modeRes.status === "fulfilled"
            ? modeRes.value.data.list.map((m: Mode) => ({
                label: m.name,
                value: m.id,
              }))
            : [],
        tecniche: rawTecniche.map((t) => ({ label: t.name, value: t.id! })),
        tecnicheRaw: rawTecniche,
      });
      setLoading(false);
    });
  }, [enabled]);

  return { options, loading, loadRepSchemesByType, loadEffortsByType };
}

// PROG-WEEK-PARAMS/FE-1 — shape passed down the SplitListView → ExerciseCard
// → ExerciseProgressionEditor chain. Built once at page level (Q5 user
// decision) so we don't mount N independent useEntityOptions instances.
export type ProgEditorOptions = {
  repSchemeTypeOptions: SelectOption[];
  effortTypeOptions: SelectOption[];
  tutOptions: SelectOption[];
  tecnicaOptions: SelectOption[];
  tecnicheRaw: Tecnica[];
  // T2.B (#eda532b5) — Recupero chip needs the rec catalog.
  recOptions: SelectOption[];
  loadRepSchemesByType: (typeId: number) => Promise<StringOption[]>;
  loadEffortsByType: (typeId: number) => Promise<StringOption[]>;
};
